import React, { useRef, useState } from "react";
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import { useTranslation } from 'react-i18next';
import { Box, Text, IconButton, Flex } from "@chakra-ui/react";
import { Image } from "@chakra-ui/react";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { motion, useInView, AnimatePresence } from 'framer-motion';
// @ts-ignore
import construction1 from "../assets/photo_bat/construction1.jpg";
// @ts-ignore
import construction2 from "../assets/photo_bat/construction2.jpg";
// @ts-ignore
import construction3 from "../assets/photo_bat/construction3.jpg";
// @ts-ignore
import construction5 from "../assets/photo_bat/construction5.jpg";
// @ts-ignore
import construction6 from "../assets/photo_bat/construction6.jpg";
// @ts-ignore
import construction7 from "../assets/photo_bat/construction7.jpg";

const MotionBox = motion(Box);

const Realisations = () => {
    const { t } = useTranslation();
    const ref = useRef(null);
    const carouselRef = useRef(null);
    const isInView = useInView(ref, { once: false, margin: "-80px" });
    const carouselInView = useInView(carouselRef, { once: true, margin: "-60px" });

    const [currentSlide, setCurrentSlide] = useState(0);

    const images = [
        { src: construction1, alt: "Réalisation 1", label: `${t("realisationsProjet")} 01` },
        { src: construction2, alt: "Réalisation 2", label: `${t("realisationsProjet")} 02` },
        { src: construction3, alt: "Réalisation 3", label: `${t("realisationsProjet")} 03` },
        { src: construction5, alt: "Réalisation 5", label: `${t("realisationsProjet")} 04` },
        { src: construction6, alt: "Réalisation 6", label: `${t("realisationsProjet")} 05` },
        { src: construction7, alt: "Réalisation 7", label: `${t("realisationsProjet")} 06` },
    ];

    // Variantes pour le header
    const headerVariants = {
        hidden: { opacity: 0, y: 28 },
        visible: (d: number) => ({
            opacity: 1,
            y: 0,
            transition: { duration: 0.85, delay: d, ease: [0.16, 1, 0.3, 1] },
        }),
    };

    return (
        <Box
            id="Produits"
            ref={ref}
            bg="#03111F"
            py={{ base: 24, md: 40 }}
            px={{ base: 6, md: 16 }}
            color="white"
            position="relative"
            overflow="hidden"
        >
            {/* ── Ambient glow décoratif ── */}
            <MotionBox
                position="absolute"
                top="-150px"
                left="50%"
                transform="translateX(-50%)"
                w="700px"
                h="350px"
                bg="radial-gradient(ellipse, rgba(201,168,76,0.06) 0%, transparent 70%)"
                pointerEvents="none"
                initial={{ opacity: 0 }}
                animate={isInView ? { opacity: 1 } : { opacity: 0 }}
                transition={{ duration: 1.5 }}
            />

            {/* ── Numéro décoratif en fond ── */}
            <MotionBox
                position="absolute"
                right="-20px"
                top="50%"
                transform="translateY(-50%)"
                pointerEvents="none"
                userSelect="none"
                initial={{ opacity: 0, x: 60 }}
                animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 60 }}
                transition={{ duration: 1.2, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            >
                <AnimatePresence mode="wait">
                    <motion.div
                        key={currentSlide}
                        initial={{
                            opacity: 0,
                            scale: 1.4,
                            rotateX: -25,
                            filter: "blur(16px)",
                            y: 40,
                        }}
                        animate={{
                            opacity: 1,
                            scale: 1,
                            rotateX: 0,
                            filter: "blur(0px)",
                            y: 0,
                        }}
                        exit={{
                            opacity: 0,
                            scale: 0.7,
                            rotateX: 20,
                            filter: "blur(20px)",
                            y: -50,
                        }}
                        transition={{
                            duration: 0.75,
                            ease: [0.16, 1, 0.3, 1],
                        }}
                        style={{ perspective: "800px", transformStyle: "preserve-3d" }}
                    >
                        <Text
                            fontSize={{ base: "120px", md: "220px" }}
                            fontWeight="900"
                            lineHeight="1"
                            letterSpacing="-0.05em"
                            bgGradient="linear(to-b, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.01) 100%)"
                            bgClip="text"
                            sx={{ WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}
                        >
                            {String(currentSlide + 1).padStart(2, "0")}
                        </Text>
                    </motion.div>
                </AnimatePresence>
            </MotionBox>

            {/* ── Header ── */}
            <Box textAlign="center" mb={16} position="relative" zIndex={1}>
                {/* Eyebrow */}
                <motion.div
                    custom={0}
                    variants={headerVariants}
                    initial="hidden"
                    animate={isInView ? "visible" : "hidden"}
                >
                    <Text
                        fontSize="10px"
                        letterSpacing="0.4em"
                        textTransform="uppercase"
                        color="whiteAlpha.400"
                        mb={5}
                        fontWeight="400"
                    >
                        {t("realisationTitre")}
                    </Text>
                </motion.div>

                {/* Titre */}
                <motion.div
                    custom={0.1}
                    variants={headerVariants}
                    initial="hidden"
                    animate={isInView ? "visible" : "hidden"}
                >
                    <Text
                        fontSize={{ base: "4xl", md: "6xl", lg: "7xl" }}
                        fontWeight="200"
                        textAlign="center"
                        letterSpacing="-0.04em"
                        lineHeight="1.05"
                        mb={6}
                    >
                        {t("realisationsHeading1")}{" "}
                        <Box as="span" fontWeight="800">
                            {t("realisationsHeading2")}
                        </Box>
                    </Text>
                </motion.div>

                {/* Séparateur animé */}
                <motion.div
                    initial={{ scaleX: 0, opacity: 0 }}
                    animate={isInView ? { scaleX: 1, opacity: 1 } : { scaleX: 0, opacity: 0 }}
                    transition={{ duration: 0.9, delay: 0.22, ease: [0.16, 1, 0.3, 1] }}
                    style={{ originX: 0.5 }}
                >
                    <Box
                        w="56px"
                        h="1px"
                        bg="linear-gradient(90deg, #C9A84C, #8B9E6E)"
                        mx="auto"
                        opacity={0.7}
                    />
                </motion.div>
            </Box>

            {/* ── Carousel + compteur ── */}
            <Box ref={carouselRef} position="relative" zIndex={1}>

                {/* Compteur de slides — haut droite */}
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={carouselInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.6, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        maxWidth: "960px",
                        margin: "0 auto 12px auto",
                        paddingRight: "4px",
                    }}
                >
                    <Flex align="center" gap={2}>
                        <AnimatePresence mode="wait">
                            <motion.span
                                key={currentSlide}
                                initial={{ opacity: 0, y: 6 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -6 }}
                                transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                                style={{
                                    fontSize: "11px",
                                    letterSpacing: "0.2em",
                                    color: "#C9A84C",
                                    fontWeight: "500",
                                }}
                            >
                                {String(currentSlide + 1).padStart(2, "0")}
                            </motion.span>
                        </AnimatePresence>
                        <Box w="24px" h="1px" bg="rgba(255,255,255,0.2)" />
                        <Text fontSize="11px" letterSpacing="0.2em" color="whiteAlpha.400" fontWeight="300">
                            {String(images.length).padStart(2, "0")}
                        </Text>
                    </Flex>
                </motion.div>

                {/* Carousel */}
                <motion.div
                    initial={{ opacity: 0, y: 50, scale: 0.97 }}
                    animate={carouselInView ? { opacity: 1, y: 0, scale: 1 } : {}}
                    transition={{ duration: 1.2, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                >
                    <Box
                        maxW="960px"
                        mx="auto"
                        borderRadius="32px"
                        overflow="hidden"
                        boxShadow="0 48px 96px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.06)"
                        position="relative"
                        sx={{
                            ".carousel-root": { borderRadius: "32px", overflow: "hidden" },
                            ".carousel":      { borderRadius: "32px", overflow: "hidden" },
                            ".slide":         { borderRadius: "32px", overflow: "hidden" },
                            ".carousel .control-dots": { display: "none" },
                        }}
                    >
                        <Carousel
                            showArrows
                            infiniteLoop
                            showStatus={false}
                            showThumbs={false}
                            autoPlay
                            interval={4500}
                            transitionTime={900}
                            swipeable
                            emulateTouch
                            selectedItem={currentSlide}
                            onChange={(i) => setCurrentSlide(i)}
                            renderArrowPrev={(clickHandler, hasPrev) => (
                                <IconButton
                                    aria-label="Précédent"
                                    icon={<FaArrowLeft size={12} />}
                                    onClick={clickHandler}
                                    position="absolute"
                                    left="20px"
                                    top="50%"
                                    transform="translateY(-50%)"
                                    zIndex="10"
                                    borderRadius="full"
                                    size="md"
                                    bg="rgba(255,255,255,0.07)"
                                    backdropFilter="blur(10px)"
                                    color="white"
                                    border="1px solid rgba(255,255,255,0.12)"
                                    isDisabled={!hasPrev}
                                    transition="all 0.35s cubic-bezier(0.16, 1, 0.3, 1)"
                                    _hover={{
                                        bg: "rgba(201,168,76,0.2)",
                                        borderColor: "rgba(201,168,76,0.5)",
                                        transform: "translateY(-50%) scale(1.08)",
                                    }}
                                />
                            )}
                            renderArrowNext={(clickHandler, hasNext) => (
                                <IconButton
                                    aria-label="Suivant"
                                    icon={<FaArrowRight size={12} />}
                                    onClick={clickHandler}
                                    position="absolute"
                                    right="20px"
                                    top="50%"
                                    transform="translateY(-50%)"
                                    zIndex="10"
                                    borderRadius="full"
                                    size="md"
                                    bg="rgba(255,255,255,0.07)"
                                    backdropFilter="blur(10px)"
                                    color="white"
                                    border="1px solid rgba(255,255,255,0.12)"
                                    isDisabled={!hasNext}
                                    transition="all 0.35s cubic-bezier(0.16, 1, 0.3, 1)"
                                    _hover={{
                                        bg: "rgba(201,168,76,0.2)",
                                        borderColor: "rgba(201,168,76,0.5)",
                                        transform: "translateY(-50%) scale(1.08)",
                                    }}
                                />
                            )}
                        >
                            {images.map((img, i) => (
                                <Box key={i} position="relative" overflow="hidden">
                                    <Image
                                        src={img.src}
                                        alt={img.alt}
                                        w="100%"
                                        objectFit="cover"
                                        display="block"
                                        style={{ filter: "saturate(0.78) brightness(0.90)" }}
                                    />
                                    {/* Overlay dégradé bas */}
                                    <Box
                                        position="absolute"
                                        bottom={0}
                                        left={0}
                                        right={0}
                                        h="35%"
                                        bg="linear-gradient(0deg, rgba(3,17,31,0.7) 0%, transparent 100%)"
                                        pointerEvents="none"
                                    />
                                    {/* Label discret en bas gauche */}
                                    <Box
                                        position="absolute"
                                        bottom={5}
                                        left={6}
                                        pointerEvents="none"
                                    >
                                        <Text
                                            fontSize="10px"
                                            letterSpacing="0.3em"
                                            textTransform="uppercase"
                                            color="whiteAlpha.500"
                                            fontWeight="400"
                                        >
                                            {img.label}
                                        </Text>
                                    </Box>
                                </Box>
                            ))}
                        </Carousel>
                    </Box>
                </motion.div>

                {/* Barre de progression des slides */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={carouselInView ? { opacity: 1 } : {}}
                    transition={{ duration: 0.6, delay: 0.8 }}
                    style={{
                        maxWidth: "960px",
                        margin: "20px auto 0",
                        display: "flex",
                        gap: "6px",
                    }}
                >
                    {images.map((_, i) => (
                        <Box
                            key={i}
                            flex="1"
                            h="1px"
                            position="relative"
                            bg="rgba(255,255,255,0.1)"
                            borderRadius="full"
                            overflow="hidden"
                            cursor="pointer"
                            onClick={() => setCurrentSlide(i)}
                        >
                            <motion.div
                                animate={{ scaleX: currentSlide === i ? 1 : 0 }}
                                initial={{ scaleX: 0 }}
                                transition={{ duration: currentSlide === i ? 4.5 : 0.3, ease: "linear" }}
                                style={{
                                    position:        "absolute",
                                    inset:           0,
                                    background:      "linear-gradient(90deg, #C9A84C, #8B9E6E)",
                                    transformOrigin: "left",
                                    borderRadius:    "full",
                                }}
                            />
                        </Box>
                    ))}
                </motion.div>
            </Box>
        </Box>
    );
};

export default Realisations;